import React, {
	useContext,
	useState,
	useMemo,
	useCallback,
} from 'react';
import { useQuery } from '@apollo/client';

import { ListContext } from '../ListContext';
import { ListInitialVariablesContext } from '../ListInitialVariablesContext';
import { resolveListQueryVariables } from '../../utils/list/resolveListQueryVariables';

const DefaultErrorComponent = () => 'Error in ListContainer';
const DefaultNoDataComponent = () => 'No data!';

/**
 * Higher-order component that provides GraphQL query functionality to list components.
 *
 * Lazy Loading Feature:
 * - When lazyLoad=true, the GraphQL query is initially skipped (not executed)
 * - Components must call triggerQuery() to execute the query (exposed via ListContext)
 * - This is useful for dropdowns/selectors that should only fetch data when opened
 * - Example: SearchDropdownSelector uses this to defer data loading until user opens the dropdown
 *
 * @param {boolean} lazyLoad - If true, query won't execute until triggerQuery() is called
 * @returns {Function} triggerQuery - Function to manually trigger the query (only when lazyLoad=true)
 */
export default (WrappedComponent) => {
	const ReturnedComponent = ({
		initialPage,
		initialPerPage,
		initialSortBy,
		initialSortDirection,
		initialFilter,
		initialExtraFields,
		initialQueryVariables,
		initialToggleableFields, // TODO: remove once all callers migrate to initialExtraFields
		query,
		queryVariablesResolver,
		getAllItems = false,
		destroyed = false,
		ErrorComponent = DefaultErrorComponent,
		NoDataComponent = DefaultNoDataComponent,
		lazyLoad = false, // If true, query won't execute until triggerQuery() is called
		...otherProps
	}) => {
		const initialVariablesOverride = useContext(ListInitialVariablesContext);
		// Track if query has been triggered (for lazy loading)
		const [hasBeenTriggered, setHasBeenTriggered] = useState(false);
		const skipQuery = lazyLoad && !hasBeenTriggered;

		const resolvedInitialExtraFields = initialExtraFields || initialToggleableFields;

		const resolvedInitialVariables = {
			...(initialQueryVariables || {}),
			sortBy: initialSortBy,
			sortDirection: initialSortDirection,
			sortByResolver: () => {},
			...(destroyed ? { destroyed } : {}),
			...(getAllItems ? {} : { page: initialPage }),			// Keep query variables clean by not passing page if getting all items
			...(getAllItems ? {} : { perPage: initialPerPage }),		// Keep query variables clean by not passing perPage if getting all items
			...(initialFilter ? { filter: initialFilter } : {}),				// Keep query variables clean - filter is not a required variable
			...(resolvedInitialExtraFields ? { extraFields: resolvedInitialExtraFields } : {}), // Used for optional/conditional query fields.
			...(initialVariablesOverride || {}),
		};

		// Variables used to query back-end
		const [variables, setVariables] = useState(resolvedInitialVariables);

		const [initialQueryVariablesState] = useState(resolvedInitialVariables);
		const resolveQueryVariables = useCallback((queryVariables) => {
			const resolvedVariables = resolveListQueryVariables(queryVariables);
			return queryVariablesResolver
				? queryVariablesResolver(resolvedVariables)
				: resolvedVariables;
		}, [queryVariablesResolver]);

		const { filter } = variables;

		const updateVariables = useCallback(newVariables => {
			return setVariables(prevVariables => ({
				...prevVariables,
				...newVariables,
			}));
		}, [setVariables]);

		// Function to trigger the query manually (for lazy loading)
		const triggerQuery = useCallback(() => {
			setHasBeenTriggered(true);
		}, []);

		const {
			data, loading, error, refetch, networkStatus,
		} = useQuery(query, {
			variables: resolveQueryVariables(initialQueryVariablesState),
			// Critical for network statuses except for 7 to show up,
			// so that we can show a spinner when loading more, etc.
			notifyOnNetworkStatusChange: true,
			errorPolicy: 'all',
			fetchPolicy: 'cache-and-network',
			// Skip the query if lazy loading is enabled and hasn't been triggered yet
			skip: skipQuery,
		});

		const fetchMore = useCallback(({ variables: newVariables }) => {
			return refetch(resolveQueryVariables(newVariables));
		}, [refetch, resolveQueryVariables]);

		// Set up helpful loading statuses
		const loadingInitial = networkStatus === 1;
		const loadingMore = !!(networkStatus > 1 && networkStatus < 7);

		// Neat tool to get the operation name, e.g. getProducts,
		// from the query. Useful, as it helps us generalise
		// extracting data, without having to rely on 'modelName'
		// and some convention in naming our queries.
		// const operationName = getOperationName(query);

		// Above not working!? Tried it from apollo-link and apollo-utilities
		const operationName = query.definitions[0].selectionSet.selections[0].name.value.toString();

		// This means items will always be an array;
		// it assists with handling loading (e.g. can map it
		// regardless of whether loading, avoids extra loading
		// checks in children component, etc.
		let items = null;
		let count = null;

		if (data && data[operationName]) {
			({ items, count } = data[operationName]);
		}

		const isFiltered = filter && Object.keys(filter).length > 0;
		const shouldGetAllItems = getAllItems || variables.perPage === undefined;

		// Set useful value which defines conditions in which
		// Child / wrapped components can filter, sort, front-end only.
		const canUseFrontEndFunctionality = (shouldGetAllItems || (!isFiltered && !loading && !loadingMore && count < variables.perPage && variables.page <= 1));

		// Function used by Apollo update query parameter when
		// fetching more, to merge new results into existing results
		const updateQuery = useCallback((previousResult, { fetchMoreResult }) => {
			if (!fetchMoreResult) {
				return previousResult;
			}
			return {
				...previousResult,
				[operationName]: {
					...previousResult[operationName],
					items: fetchMoreResult[operationName].items,
					count: fetchMoreResult[operationName].count,
				},
			};
		}, [operationName]);

		const fetchMoreWithSameVariables = useCallback(() => {
			return fetchMore({
				variables: resolveListQueryVariables(variables), // Uses current variables without internal list-only keys
				updateQuery,
			});
		}, [fetchMore, variables, updateQuery]);

		const context = useMemo(() => ({

			query, // Required e.g. with mutations in the list

			// !!! Everytime we refetch, we actually want to do this. So, am using this for now.
			// To simply use refetch, we would have to change the double state above (maybe it's fine).
			refetch: fetchMoreWithSameVariables,
			fetchMore,
			updateQuery,
			variables,
			updateVariables,
			setVariables,

			count,
			error,
			loading,
			loadingInitial,
			loadingMore,
			operationName,
			items,

			canUseFrontEndFunctionality,
			getAllItems,

			initialFilter,

			// Lazy loading support
			triggerQuery,
			lazyLoad,
			hasBeenTriggered,
		}), [query, variables, updateVariables, setVariables, updateQuery, count, error, loading, loadingInitial, loadingMore, fetchMore,
			operationName, items, canUseFrontEndFunctionality, getAllItems, initialFilter, fetchMoreWithSameVariables, triggerQuery, lazyLoad, hasBeenTriggered]);

		if (error && !data) {
			console.log(error);
			return <ErrorComponent />;
		}

		// Don't show NoDataComponent if we're in lazy-load skip mode (waiting for user to trigger)
		const isLazyLoadSkipping = lazyLoad && !hasBeenTriggered;
		if (!data && !loading && !isLazyLoadSkipping) {
			return <NoDataComponent />;
		}

		return (
			<ListContext.Provider value={context}>
				<WrappedComponent

					// !!! Everytime we refetch, we actually want to do this. So, am using this for now.
					// To simply use refetch, we would have to change the double state above (maybe it's fine).
					refetch={fetchMoreWithSameVariables}

					// Passing this to check with branch in list container
					// whether to apply front-end or back-end list actions
					query={query}
					initialSortBy={initialSortBy}
					initialSortDirection={initialSortDirection}
					initialPage={initialPage}
					initialPerPage={initialPerPage}
					initialQueryVariables={initialQueryVariables}
					{...otherProps}
				/>
			</ListContext.Provider>
		);
	};

	return ReturnedComponent;
};
